// Copyright (c) 2026 杨翔宇（武汉文理学院）
// 物理智能教学智能体平台 (Physics Intelligent Agent)
// 保留所有权利 (All Rights Reserved)。未经著作权人书面许可，
// 不得以任何形式复制、传播、修改或用于商业用途。

/**
 * chatStore UI-action slice (AI-3 Lane, A-9/A-10).
 *
 * Extracts the ``sendUiAction`` responsibility out of
 * ``chatStore.ts`` into a peer module following the same
 * factory pattern as ``createThreadReplaySlice``.
 *
 * The slice is composed into the main store via
 * ``sendUiAction: createUiActionSlice(set, get).sendUiAction``
 * so consumer code is unchanged.
 */
import { extractUserFacingMessage } from '@/api/errorMessage';
import { api } from '@api/client';
import { isEmptyUiActionPatch, normalizeUiActionRuntimeEvents } from '@utils/chatRuntimeEvents';
import { applyUiStatePatchToMessages } from '@/pages/main-chat-runtime/transcript/mainChatTranscriptReconciler';
import { selectCurrentThreadId } from './chatStore';
import { applyUiActionPatchToThreadView } from './chatStore.threadView';


/** Public type of the UI-action method. */
export interface UiActionMethods {
  sendUiAction: (
    messageId: string,
    blockId: string,
    action: string,
    params?: Record<string, unknown>,
  ) => Promise<Record<string, unknown> | undefined>;
}


/**
 * Factory function for the UI-action slice.
 *
 * Follows the same pattern as ``createThreadReplaySlice``:
 * the main store passes its own ``set`` / ``get`` and the
 * returned methods are spread into the store definition.
 */
export function createUiActionSlice(
  set: (partial: any, replace?: boolean) => void,
  get: () => any,
): UiActionMethods {
  return {
    sendUiAction: async (messageId, blockId, action, params) => {
      const snapshot = get();
      // CD-5: the dispatch thread id is resolved through the single canonical
      // selector authority (CD-3), not the persisted top-level field.  The slice
      // no longer couples the dispatch path to the redundant `currentThreadId`
      // state field.
      const threadId = selectCurrentThreadId(snapshot);
      const currentSession = snapshot.currentSession;
      if (!threadId || !currentSession) return;

      try {
        set({ chatError: null });
        const { data } = await api.sendUiAction({
          thread_id: threadId,
          message_id: messageId,
          block_id: blockId,
          action,
          params,
        });
        const events = normalizeUiActionRuntimeEvents(data);
        if (events.length === 0 || isEmptyUiActionPatch(events[0].patch)) {
          set({ chatError: 'UI 操作响应异常，请重试' });
          throw new Error('UI 操作响应异常，请重试');
        }
        const uiPatch = events.find(
          (event) => event.type === 'ui_state_patch'
        );
        const patch = uiPatch?.type === 'ui_state_patch' ? uiPatch.patch : undefined;
        const targetMessageId = patch?.messageId || messageId;

        const current = get();
        if (selectCurrentThreadId(current) !== threadId) {
          throw new Error('UI 操作所属线程已切换');
        }
        const normalizedMessages = applyUiStatePatchToMessages(
          current.messages, targetMessageId, patch, { threadId, blockId },
        );
        if (!normalizedMessages) {
          throw new Error('UI 操作已生效，但当前卡片未同步；请刷新会话');
        }
        const patchedIndex = normalizedMessages.findIndex(
          (message, index) => message !== current.messages[index],
        );
        const localTargetMessageId = normalizedMessages[patchedIndex]?.id ?? targetMessageId;
        const nextView = current.currentThreadView
            ? applyUiActionPatchToThreadView(current.currentThreadView, {
                threadId,
                targetMessageId: localTargetMessageId,
                patchedMessages: normalizedMessages,
              })
            : current.currentThreadView;
        set((state: any) => ({
            messages: normalizedMessages,
            chatError: null,
            currentThreadView: nextView,
            messagesBySession: {
              ...state.messagesBySession,
              [currentSession.session_id]: normalizedMessages,
            },
          }));
        void get().loadThreadTasks(threadId);
        return data;
      } catch (error) {
        set({ chatError: extractUserFacingMessage(error) });
        // D-007（病族 #1）：错误必须上抛，调用方（useUiActionDispatcher）
        // 依赖它做 409 stale_ui_target 自愈与用户可见提示；吞掉即静默失败。
        throw error;
      }
    },
  };
}
